import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, Copy, Landmark } from "lucide-react"
import { type ReactNode, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import {
  type DatoPago,
  type DatosPagoProveedor,
  NOMBRE_TIPO,
  paraElMetodo,
  type TipoDatoPago,
} from "@/componentes/catalogos/datosPago"
import { copiar } from "@/componentes/credenciales/portapapeles"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"

export const claveDatosPago = (proveedorId: number) => ["proveedor-datos-pago", proveedorId]

export function useDatosPago(proveedorId: number) {
  return useQuery({
    queryKey: claveDatosPago(proveedorId),
    queryFn: () => api.get<DatosPagoProveedor>(`/api/v1/proveedores/${proveedorId}/datos-pago`),
  })
}

/**
 * Un dato de pago tal cual se le pasa al banco: sus líneas, cada una con su «copiar», y
 * «Copiar todo» con el texto que armó el servidor.
 */
export function TarjetaDatoPago({ dato, acciones }: { dato: DatoPago; acciones?: ReactNode }) {
  const [copiado, setCopiado] = useState<string | null>(null)

  async function copiarCon(texto: string, hecho: string) {
    setCopiado((await copiar(texto)) ? hecho : "No se pudo copiar")
    window.setTimeout(() => setCopiado(null), 2500)
  }

  return (
    <article className="tarjeta space-y-2 p-3" data-testid={`dato-pago-${dato.id}`}>
      <header className="flex flex-wrap items-center gap-2">
        <Landmark size={16} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h4 className="min-w-0 flex-1 text-sm font-semibold break-words">{dato.titulo}</h4>
        {dato.preferido && <Insignia tono="info">Preferido</Insignia>}
        {acciones}
      </header>

      {dato.avisos.map((aviso) => (
        <p
          key={aviso}
          className="flex items-start gap-1.5 text-xs"
          style={{ color: "var(--warning)" }}
        >
          <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden={true} /> {aviso}
        </p>
      ))}

      <dl className="space-y-1 text-sm">
        {dato.lineas.map((linea) => (
          <div key={linea.rotulo} className="flex items-start gap-2">
            <dt className="w-24 shrink-0 text-xs leading-5" style={{ color: "var(--fg-muted)" }}>
              {linea.rotulo}
            </dt>
            <dd className="min-w-0 flex-1 break-all tabular-nums">{linea.valor}</dd>
            <button
              type="button"
              aria-label={`Copiar ${linea.rotulo}`}
              title={`Copiar ${linea.rotulo}`}
              onClick={() => copiarCon(linea.valor, `${linea.rotulo} copiado`)}
              className="foco-cristal toque-cristal -my-1 inline-flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
            >
              <Copy size={14} aria-hidden={true} />
            </button>
          </div>
        ))}
      </dl>

      {dato.notas && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {dato.notas}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Boton tamano="sm" onClick={() => copiarCon(dato.texto, "Datos copiados")}>
          <Copy size={14} aria-hidden={true} /> Copiar todo
        </Boton>
        <span role="status" className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {copiado}
        </span>
      </div>
    </article>
  )
}

/**
 * Los datos del proveedor para pagarle, donde se va a pagar (03/11, pedido del usuario):
 * el modal de pago y la orden aprobada.
 *
 * ⚠ **Con `metodo`, enseña los que sirven a ESE método** —el pago móvil al elegir pago
 * móvil— y cambia al cambiarlo. Sin método elegido, todos. Sin `metodo` (la orden
 * aprobada, donde aún no se eligió), se elige aquí por clase de dato.
 *
 * ⚠ **Sin permiso no se pinta nada.** Lo abren `proveedor:ver` y `tesoreria:ejecutar_pago`;
 * a quien no tiene ninguno el servidor le responde 403 y aquí no hay nada que enseñarle.
 */
export function DatosParaPagar({
  proveedorId,
  metodo,
}: {
  proveedorId: number
  metodo?: { id: number | null; nombre?: string }
}) {
  const consulta = useDatosPago(proveedorId)
  const [verTodos, setVerTodos] = useState(false)
  const [elegido, setElegido] = useState<TipoDatoPago | null>(null)

  if (consulta.isPending) return null
  if (consulta.isError) {
    if (consulta.error instanceof ErrorApi && consulta.error.estado === 403) return null
    return (
      <EstadoError
        titulo="No se pudieron cargar los datos de pago del proveedor."
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }

  const respuesta = consulta.data
  const todos = respuesta.datos
  const tipos = [...new Set(todos.map((d) => d.tipo))]
  const tipo = elegido ?? tipos[0] ?? null

  let visibles: DatoPago[] | null
  if (metodo !== undefined) visibles = verTodos ? todos : paraElMetodo(respuesta, metodo.id)
  else visibles = todos.filter((d) => d.tipo === tipo)

  const nombreMetodo = metodo?.nombre ?? "este método"

  return (
    <section className="space-y-2" aria-label="Datos para pagar al proveedor">
      <h3 className="text-sm font-semibold">Datos para pagar a {respuesta.nombre}</h3>

      {metodo === undefined && tipos.length > 1 && (
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Pagar por</legend>
          {tipos.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={t === tipo}
              onClick={() => setElegido(t)}
              className={`foco-cristal h-9 rounded-[var(--radius-control)] border px-3 text-sm ${t === tipo ? "activo-cristal font-medium" : "toque-cristal"}`}
            >
              {NOMBRE_TIPO[t]}
            </button>
          ))}
        </fieldset>
      )}

      {todos.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          No tiene datos de pago registrados. Se cargan en su ficha de proveedor, en «Datos de
          pago».
        </p>
      ) : visibles === null ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {metodo?.nombre ?? "Este método"} no usa datos bancarios del proveedor.
        </p>
      ) : visibles.length === 0 ? (
        <div className="space-y-1 text-sm">
          <p style={{ color: "var(--fg-muted)" }}>
            No tiene datos registrados para {nombreMetodo}. Tiene{" "}
            {tipos.map((t) => NOMBRE_TIPO[t].toLowerCase()).join(", ")}.
          </p>
          <Boton tamano="sm" onClick={() => setVerTodos(true)}>
            Ver los que tiene
          </Boton>
        </div>
      ) : (
        <div className="grid gap-2">
          {visibles.map((dato) => (
            <TarjetaDatoPago key={dato.id} dato={dato} />
          ))}
        </div>
      )}

      {metodo !== undefined && verTodos && (
        <Boton tamano="sm" variante="fantasma" onClick={() => setVerTodos(false)}>
          Sólo los de {nombreMetodo}
        </Boton>
      )}
    </section>
  )
}
